// dashboard.component.ts
import { Component, OnInit } from '@angular/core';
import { downloadBlob } from '../../utils/blob-viewer.util';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';

import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { MeasureDetailsComponent } from '@components/measuredetails/measure-details.component';
import { sampleMeasures, sampleProviders } from './sampleData';
import { PatientMeasureDetailsComponent } from '@components/measurepatientdetails/patient-measure-details.component';
import { mockPatientMeasures } from '@components/measurepatientdetails/mock-patient-measures';

import { MipsGaugesComponent } from '@components/mipsgauage/mips-gauges.component';
import { sampleGauges } from '@components/mipsgauage/gauage-mockdata';

import { DashboardProvider } from '@models/dashboard-providers.model';
import { DashboardMeasureDetail } from '@models/dashboard-measure-details.model';
import { DashboardMeasurePatient } from '@models/dashboard-measure-patient.model';
import { GaugeData } from '@models/gauges.model';
import { MeasureMonthlyTrendComponent } from '@components/measure-monthly-trend/measure-monthly-trend.component';




@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    MatIconModule,
    MatSlideToggleModule,
    MatCardModule,
    CommonModule,
    MatSelectModule,
    MatButtonModule,
    MatTabsModule,
    FormsModule,
    MeasureDetailsComponent,
    PatientMeasureDetailsComponent,
    MipsGaugesComponent,
    MatCheckboxModule,
    MatTooltipModule,
    MeasureMonthlyTrendComponent
  ],
  templateUrl: './dashboard-demo.component.html',
  styleUrls: ['./dashboard-demo.component.css']
})
export class DashboardDemoComponent implements OnInit {
  showBenchmarks = true;
  selectedTab = 'all';
  selectedDetailsTab = 'performanceData';
  showMeasureDetails = false;
  showPatientDetails = false;
  selectedIndex: number | null = null; // Track which row was selected
  refreshDate = '5/1/2025'; //
  isSmallPractice = false;

  // **********Sample data ************/
  measures: DashboardMeasureDetail[] = sampleMeasures
  uniqueMeasures = this.getUniqueMeasuresById(this.measures);
  //sort by measure name
  measurePatients = mockPatientMeasures;
  gauges: GaugeData[] = sampleGauges;
  providers: DashboardProvider[] = sampleProviders;

  years = ["2025", "2024"];
  //****** End Sample Data ************/


  // Create a filtered array of measures based on tab selection
  filteredMeasures: DashboardMeasureDetail[] = [...this.measures];

  selectedMeasure: DashboardMeasureDetail = this.measures[0];



  filters = {
    measureSelection: ['all'] as string[], // Explicitly define the type as string[]
    clinician: 'all',
    location: '',
    selectGroup: '',
    measureYear: this.years[0],
  };

  constructor() { }

  ngOnInit(): void {
    // Initialize with all measures
    this.applyTabFilter('all');
  }

  toggleBenchmarks(): void {
    this.showBenchmarks = !this.showBenchmarks;
  }


  selectTab(tab: string): void {
    this.selectedTab = tab;
    this.applyTabFilter(tab);

    // Reset measure details when changing tabs
    this.closeDetails();
  }

  // Filter measures based on tab selection
  applyTabFilter(tab: string): void {
    // First apply the tab filter
    let filteredResults = [];
    switch (tab) {
      case 'mips measures':
        filteredResults = this.measures.filter(measure => measure.isMIPS);
        break;
      case 'mvp measures':
        filteredResults = this.measures.filter(measure => measure.isMVP == true);
        break;
      case 'all':
      default:
        filteredResults = [...this.measures];
        break;
    }

    // Then apply additional filters from the filters object
    if (this.filters.clinician && this.filters.clinician === 'all') {
      filteredResults = filteredResults.filter(measure => {
        // Filter by measure year
        if (this.filters.measureYear && measure.performanceYear?.toString() !== this.filters.measureYear) {
          return false;
        }
        return true;
      });




      var aggregateMeasures = this.aggregateMeasures(filteredResults);
      aggregateMeasures.sort((a, b) => a.name?.localeCompare(b.name ?? '') || 0);

      this.filteredMeasures = aggregateMeasures.filter(measure => {



        // Filter by selected measures, if any
        if (
          //select filter.measureSelection where id is not all
          this.filters.measureSelection.filter(id => id !== 'all').length > 0 &&
          !this.filters.measureSelection.includes(measure.id)
        ) {
          return false;
        }

        return true;
      });

    }
    else {
      this.filteredMeasures = filteredResults.filter(measure => {
        // Filter by clinician/provider
        if (this.filters.clinician && measure.providerId !== this.filters.clinician) {
          return false;
        }

        // Filter by measure year
        if (this.filters.measureYear && measure.performanceYear?.toString() !== this.filters.measureYear) {
          return false;
        }

        // Filter by selected measures, if any
        if (
          //select filter.measureSelection where id is not all
          this.filters.measureSelection.filter(id => id !== 'all').length > 0 &&
          !this.filters.measureSelection.includes(measure.id)
        ) {
          return false;
        }

        return true;
      });

      this.filteredMeasures.sort((a, b) => a.name?.localeCompare(b.name ?? '') || 0);

    }
  }

  // Filter measures based on tab selection
  getAvailableMeasures(): DashboardMeasureDetail[] {
    // First apply the tab filter
    let availablemeasures = [];
    switch (this.selectedTab.toLowerCase()) {
      case 'mips measures':
        availablemeasures = this.measures.filter(measure => measure.isMIPS);
        break;
      case 'mvp measures':
        availablemeasures = this.measures.filter(measure => measure.isMVP);
        break;
      case 'all':
      default:
        availablemeasures = [...this.measures];
        break;
    }

    // Then apply additional filters from the filters object
    return availablemeasures.filter(measure => {
      // Filter by clinician/provider
      if (this.filters.clinician && measure.providerId !== this.filters.clinician) {
        return false;
      }

      // Filter by measure year
      if (this.filters.measureYear && measure.performanceYear?.toString() !== this.filters.measureYear) {
        return false;
      }


      return true;
    });
  }

  getMeasurePatients(): DashboardMeasurePatient[] {

    //if clinician is all, return all patients limited to selected measures
    if (this.filters.clinician === 'all') {
      return this.measurePatients.filter(patient => {

        // Filter by selected measures, if any
        if (this.selectedMeasure.id && patient.measureID !== this.selectedMeasure.id) {
          return false;
        }


        return true;
      });
    }
    else {
      // Then apply additional filters from the filters object
      return this.measurePatients.filter(patient => {
        // Filter by clinician/provider
        if (this.filters.clinician && patient.providerID !== this.filters.clinician) {
          return false;
        }

        // Filter by measure year
        //not there in the patient data yet


        // Filter by selected measures, if any
        if (this.selectedMeasure.id && patient.measureID !== this.selectedMeasure.id) {
          return false;
        }


        return true;
      });
    }



  }

  getUniqueMeasuresById(measures: any[]): any[] {
    const seen = new Set();
    return measures.filter(measure => {
      if (seen.has(measure.id)) {
        return false;
      }
      seen.add(measure.id);
      return true;
    });
  }

  applyFilters(): void {
    // Apply the form filters first
    console.log('Filters applied:', this.filters);
    

    // Then re-apply the tab filter to ensure we maintain the tab selection
    this.applyTabFilter(this.selectedTab);
    this.selectedMeasure = this.filteredMeasures[0];
    this.selectedIndex = null;

  }

  getColor(measure:string,value: number, benchmark: number, isInverse: boolean): string {
    if (isInverse) {
      return this.getInverseMeasureColor(measure,value, benchmark);
    } else {
      return this.getNormalMeasureColor(measure,value, benchmark);
    }
  
  }
    isInverseMeasure(measure: DashboardMeasureDetail): boolean {
     if (measure.inverse === undefined) {
      return false; // Default to false if inverse is not defined   
     }

    return measure.inverse.toLowerCase() === 'inverse';
  }

  getNormalMeasureColor(measure:string,value: number, benchmark: number): string {
    if (value >= benchmark) {
      return '#0099AB'; // Blue
    } else if (value < benchmark && value >= benchmark - .1) {
      return '#FF9800'; // Yellow/Orange
    } else {
      return '#BF311A'; // Red
    }
  }

  getInverseMeasureColor(measure:string, value: number, benchmark: number): string {
    if (value >= benchmark) {
      return '#BF311A'; // Red
    } else {
      return '#0099AB'; // Blue
    }
  }


hasSmallDenominator(measure: DashboardMeasureDetail): boolean {
  // Check if denominator exists and is less than 20
  return measure.denominator !== undefined && measure.denominator < 20;
}

// Handle small practice checkbox change
  onSmallPracticeChange(): void {
  
    
    // If measure details are open, trigger a re-render with the new small practice status
    if (this.showMeasureDetails && this.selectedMeasure) {
      // Create a new reference to trigger change detection
      this.selectedMeasure = { ...this.selectedMeasure };
    }
  }



  // Updated method to handle measure row click - now includes index
  onMeasureClick(measure: DashboardMeasureDetail, index: number): void {
    // If the same row is clicked again, toggle the details visibility
    if (this.selectedIndex === index && this.showMeasureDetails) {
      this.closeDetails();
      return;
    }
    
    this.selectedDetailsTab = 'performanceData';
    this.selectedMeasure = measure;
   
    this.selectedIndex = index;
    this.showMeasureDetails = true;
  }

  // Method to close details panel
  closeDetails(): void {
    this.showMeasureDetails = false;
    this.selectedIndex = null;
  }

  onViewPatients(measureId: string): void {
    this.showMeasureDetails = false;
    this.showPatientDetails = true;

    // Optionally, you might want to fetch patient data specific to the measure
    // this.fetchPatientDataForMeasure(measureId);
  }

  closePatientDetails(): void {
    this.showPatientDetails = false;
    this.showMeasureDetails = true; // Show measure details again
  }

  onExportDetails(): void {
    const exportPatients = this.selectedMeasure.id ?
      this.measurePatients.filter(patient => patient.measureID === this.selectedMeasure.id) : this.measurePatients;

    this.exportDetailsCSV(exportPatients, this.selectedMeasure.name + '_' + "MeasurePatients.csv");
  }

  exportDetailsCSV(data: DashboardMeasurePatient[], filename = "MeasurePatients.csv") {
    const csv = this.convertDetailsToCSV(data);
    const blob = new Blob([csv], { type: "text/csv" });
    downloadBlob(blob, filename);
  }

  convertDetailsToCSV(data: DashboardMeasurePatient[]): string {
    const flatData = data.map(this.flattenPatients);
    const headers = Object.keys(flatData[0]);
    const rows = flatData.map(obj => headers.map(key => `"${String(obj[key]).replace(/"/g, '""')}"`).join(','));
    return [headers.join(','), ...rows].join('\r\n');
  }

  flattenPatients(patient: DashboardMeasurePatient): Record<string, any> {
    return {
      patientID: patient.patientID,
      providerName: patient.providerName,
      firstName: patient.firstName,
      lastName: patient.lastName,
      measureId: patient.measureID,
      location: patient.location,
      measureName: patient.measureName,
      gender: patient.gender,
      age: patient.age,
      encounterDate: patient.encounterDate,
      den: patient.den,
      denExcl: patient.denExcl,
      num: patient.num,
      diagCode: patient.diagCode,
      diagDesc: patient.diagDesc,
      procedureCode: patient.procedureCode,
      procedureDate: patient.procedureDate,
      medication: patient.medication,
      medicationDate: patient.medicationDate,
    };
  }


  onExport(): void {
    console.log('Export details for measure:', this.selectedMeasure.id);
    this.exportMeasuresCSV(this.measures, 'measures.csv');
  }
  exportMeasuresCSV(data: DashboardMeasureDetail[], filename = "measures.csv") {
    const csv = this.convertMeasuresToCSV(data);
    const blob = new Blob([csv], { type: "text/csv" });
    downloadBlob(blob, filename);
  }
  flattenMeasure(measure: DashboardMeasureDetail): Record<string, any> {
    return {
      id: measure.id,
      name: measure.name ?? '',
      value: measure.value,
      benchmark_national: measure.benchmark?.national ?? '',
      benchmark_registry: measure.benchmark?.registry ?? '',
      benchmark_academic: measure.benchmark?.academic ?? '',
      registryId: measure.registryId ?? '',
      qualityId: measure.qualityId ?? '',
      measureName: measure.measureName ?? '',
      performance: measure.performance ?? '',
      performanceLastYear: measure.performanceLastYear ?? '',
      points: measure.points ?? '',
      isMIPS: measure.isMIPS ?? '',
      isMVP: measure.isMVP ?? '',
      url: measure.url ?? '',
    };
  }

  convertMeasuresToCSV(data: DashboardMeasureDetail[]): string {
    const flatData = data.map(this.flattenMeasure);
    const headers = Object.keys(flatData[0]);
    const rows = flatData.map(obj => headers.map(key => `"${String(obj[key]).replace(/"/g, '""')}"`).join(','));
    return [headers.join(','), ...rows].join('\r\n');
  }

  selectDetailsTab(tab: string): void {
    if (tab === 'mips points data') {
      this.selectedDetailsTab = 'performanceData';
    }
    else {
      this.selectedDetailsTab = 'patientData';
    }


  }

  get allSelected(): boolean {
    //select filter.measureSelection where id is not null
    return this.filters.measureSelection.filter(id => id !== 'all').length == 0;
  }

  toggleAllSelection(): void {
    this.filters.measureSelection = ['all'];
  }

  get allProvidersSelected(): boolean {
    //select filter.measureSelection where id is not null
    return this.filters.clinician === 'all';
  }
  toggleAllProviders(): void {
    this.filters.clinician = 'all';
  }


  itemClicked(): void {
    if (this.filters.measureSelection.length == 0) {
      this.filters.measureSelection = ['all'];
    }
    else if (this.filters.measureSelection.length > 1) {
      this.filters.measureSelection = this.filters.measureSelection.filter(id => id !== 'all');
    }

  }

  aggregateMeasures(data: DashboardMeasureDetail[]): DashboardMeasureDetail[] {
    const aggregationMap: { [measureID: string]: DashboardMeasureDetail } = {};

    for (const record of data) {
      if (!aggregationMap[record.id]) {
        // Clone the first record for the measure, but reset sums and provider
        aggregationMap[record.id] = {
          ...record,
          providerId: null,
          numerator: record.numerator || 0,
          denominator: record.denominator || 0,
          performance: 0,
          value: 0,
        };
      } else {
        aggregationMap[record.id].numerator += record.numerator || 0;
        aggregationMap[record.id].denominator += record.denominator || 0;
      }
    }

    // After summing, calculate performance and value
    for (const measureID in aggregationMap) {
      const aggregated = aggregationMap[measureID];
      if (aggregated.denominator > 0) {
        aggregated.performance = aggregated.numerator / aggregated.denominator;
        aggregated.value = aggregated.numerator / aggregated.denominator;
      } else {
        aggregated.performance = 0;
        aggregated.value = 0;
      }
    }

    // Return as an array
    return Object.values(aggregationMap);
  }
}
